import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/ui/Modal';
import { useCreateTakeawayMutation, type WaiterTakeawayNote } from './waiterApi';

/**
 * Spec §1.7 / §2 — payload handed back to NotesListPage on a successful
 * « + » creation: enough to both `setTicket(...)` (skip the ticket screen's
 * self-join on first mount) and navigate to `/serveur/ticket/<sessionUuid>`.
 */
export interface CreatedTicket {
  sessionUuid: string;
  kind: 'dine_in' | 'takeaway';
  label: string;
  covers: number;
  openedAt: string;
  guestToken: string;
  /** SC-976 — design §5.3: carries through to `activeTicket` so the ticket
   *  header can show the ⛓ mark without re-deriving it from the label. */
  isMerged?: boolean;
}

/** SC-1466 — how long the answer to the « Plus tard » question stays inert
 *  once it shows: a double tap, or a thumb already on its way to Valider,
 *  must not answer it. */
const LATER_CONFIRM_ARM_MS = 600;

interface Props {
  open: boolean;
  token: string;
  takeawayNotes: WaiterTakeawayNote[];
  onClose: () => void;
  onCreated: (ticket: CreatedTicket) => void;
}

/**
 * Spec 04-ux-mobile-spec.md §1b, 06-test-cases.md W-L-23..29 — the « + »
 * creation modal of the notes list. Only Emporter has one: the Sur place
 * « + Nouvelle table » (spec 2026-08-28 D3, « saisie du numéro + puces des
 * tables libres + couverts ») was removed, so a table is opened by tapping
 * its tile in the grid — which lists every table — and nowhere else. The
 * fields live in their own inner component so closing the Modal (which
 * unmounts its children, `Modal.tsx`'s `if (!open) return null`) resets the
 * form for free on the next open (W-L-21) rather than needing manual reset
 * code.
 */
export default function NoteCreateModals({ open, token, takeawayNotes, onClose, onCreated }: Props) {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onClose={onClose}
      ariaLabel={t('waiter.notes.createTakeaway.title')}
      instant
      dismissOnBackdrop={false}
    >
      {open && (
        <CreateTakeawayModalBody
          token={token}
          takeawayNotes={takeawayNotes}
          onClose={onClose}
          onCreated={onCreated}
        />
      )}
    </Modal>
  );
}

interface TakeawayBodyProps {
  token: string;
  takeawayNotes: WaiterTakeawayNote[];
  onClose: () => void;
  onCreated: (ticket: CreatedTicket) => void;
}

function CreateTakeawayModalBody({ token, takeawayNotes, onClose, onCreated }: TakeawayBodyProps) {
  const { t } = useTranslation();
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  // SC-1466 — « Plus tard » used to open the note on the first tap, from the
  // spot where Annuler / Valider had always been: waiters used to the old
  // modal hit it by habit and left with a « Client N » note. The tap now only
  // asks; the answer is what opens the note.
  const [confirmLater, setConfirmLater] = useState(false);
  const [laterArmed, setLaterArmed] = useState(false);
  useEffect(() => {
    if (!confirmLater) return;
    const id = window.setTimeout(() => setLaterArmed(true), LATER_CONFIRM_ARM_MS);
    return () => window.clearTimeout(id);
  }, [confirmLater]);
  const [createTakeaway, { isLoading }] = useCreateTakeawayMutation();

  // SC-1466 — `later` (« Plus tard ») : the client is at the counter, the
  // order comes first. The server names the note « Client N » and the ticket
  // asks for the real name once the order is sent.
  const handleSubmit = async (later = false) => {
    const trimmed = label.trim();
    if (!later && !trimmed) {
      setError(t('waiter.notes.errors.emptyTakeawayLabel'));
      return;
    }
    // [decision] W-L-25 — refused client-side against the last poll of OPEN
    // notes, trimmed + case-insensitive; the server re-checks with 409
    // LABEL_IN_USE (design.md §2) for the race the poll cannot see.
    const duplicate =
      !later && takeawayNotes.some((n) => n.label.trim().toLowerCase() === trimmed.toLowerCase());
    if (duplicate) {
      setError(t('waiter.notes.errors.labelInUse'));
      return;
    }
    setError(null);
    try {
      const result = await createTakeaway(
        later ? { provisional: true, token } : { label: trimmed, token },
      ).unwrap();
      onCreated({
        sessionUuid: result.session.uuid,
        kind: 'takeaway',
        label: result.session.label,
        covers: result.session.covers,
        openedAt: result.session.openedAt,
        guestToken: result.guestToken,
      });
    } catch (err) {
      const apiErr = err as { status?: number; data?: { code?: string } };
      if (apiErr.status === 409 || apiErr.data?.code === 'LABEL_IN_USE') {
        setError(t('waiter.notes.errors.labelInUse'));
      } else {
        setError(t('waiter.notes.errors.generic'));
      }
    }
  };

  const errorLine = error && (
    <p data-testid="create-error" role="alert" className="font-mono text-sm text-red-600">
      {error}
    </p>
  );

  // The two views carry their own `key`: same tags in the same slots, React
  // would otherwise patch Annuler / Valider into Retour / Ouvrir in place.
  if (confirmLater) {
    return (
      <div
        key="later-confirm"
        data-testid="create-takeaway-later-confirm"
        className="p-5 flex flex-col gap-4 md:max-w-[400px] md:mx-auto md:w-full"
      >
        <h2 className="font-sans text-[18px] font-bold">{t('waiter.notes.createTakeaway.laterConfirmTitle')}</h2>
        <p className="font-sans text-base text-ink-mute">{t('waiter.notes.createTakeaway.laterConfirmBody')}</p>
        {errorLine}
        <div className="flex gap-3">
          <button
            type="button"
            data-testid="create-takeaway-later-back"
            autoFocus
            onClick={() => {
              setError(null);
              setLaterArmed(false);
              setConfirmLater(false);
            }}
            className="flex-1 min-h-12 py-1 bg-paper border-2 border-ink rounded-btn px-4 font-sans text-[18px] font-bold leading-tight"
          >
            {t('common.back')}
          </button>
          <button
            type="button"
            data-testid="create-takeaway-later-confirm-button"
            disabled={!laterArmed || isLoading}
            aria-busy={isLoading}
            onClick={() => void handleSubmit(true)}
            className="flex-[2] min-h-12 py-1 bg-ink text-paper rounded-btn px-4 font-sans text-[18px] font-bold leading-tight disabled:opacity-50"
          >
            {t('waiter.notes.createTakeaway.laterConfirmButton')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div key="form" data-testid="create-takeaway-modal" className="p-5 pb-3 flex flex-col gap-4 md:max-w-[400px] md:mx-auto md:w-full">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-sans text-[18px] font-bold">{t('waiter.notes.createTakeaway.title')}</h2>
        <button
          type="button"
          data-testid="create-close"
          aria-label={t('common.close')}
          onClick={onClose}
          className="-me-2 w-11 h-11 shrink-0 flex items-center justify-center font-sans text-2xl leading-none text-ink"
        >
          ×
        </button>
      </div>
      <input
        data-testid="create-takeaway-input"
        type="text"
        inputMode="text"
        autoCapitalize="words"
        autoComplete="off"
        enterKeyHint="done"
        maxLength={30}
        autoFocus
        placeholder={t('waiter.notes.createTakeaway.placeholder')}
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        className="border-2 border-ink rounded-btn-sm px-3 py-2 text-lg bg-paper text-ink"
      />
      {errorLine}
      <div className="flex gap-3">
        <button
          type="button"
          data-testid="create-cancel"
          onClick={onClose}
          className="flex-1 h-12 bg-paper border-2 border-ink rounded-btn px-4 font-sans text-[18px] font-bold"
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          data-testid="create-submit"
          disabled={isLoading}
          aria-busy={isLoading}
          onClick={() => void handleSubmit()}
          className="flex-1 h-12 bg-ink text-paper rounded-btn px-4 font-sans text-[18px] font-bold disabled:opacity-50"
        >
          {t('waiter.notes.submit')}
        </button>
      </div>
      {/* Below Annuler / Valider, set apart and small: out of the way of a
          thumb aiming for Valider. */}
      <div className="mt-2 pt-2 border-t border-hairline flex">
        <button
          type="button"
          data-testid="create-takeaway-later"
          disabled={isLoading}
          onClick={() => {
            setError(null);
            setConfirmLater(true);
          }}
          className="min-h-[44px] px-0 font-sans text-base text-ink-mute underline underline-offset-2 text-start leading-tight disabled:opacity-50"
        >
          {t('waiter.notes.createTakeaway.later')}
        </button>
      </div>
    </div>
  );
}
